import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../../Utils/axiosInstance";
import { DEFAULT_YEAR, formatDashboardTitle } from "../utils";
export const useCashFlowDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboard, selectedProcess } = location.state || {};

  const [monthPeriods, setMonthPeriods] = useState([]);
  const [weekPeriods, setWeekPeriods] = useState([]);
  const [selectedMonthPeriod, setSelectedMonthPeriod] = useState(null);
  const [selectedWeekPeriod, setSelectedWeekPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);

  // MIS Report tab state
  const [misSelectedMonthPeriod, setMisSelectedMonthPeriod] = useState(null);
  const [misSelectedYear, setMisSelectedYear] = useState(DEFAULT_YEAR);
  const [misReportData, setMisReportData] = useState(null); // Placeholder for data
  const [misLoading, setMisLoading] = useState(false);
  const [misError, setMisError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Overview tab state
  const [overviewMonths, setOverviewMonths] = useState([]);
  const [overviewSelectedMonth, setOverviewSelectedMonth] = useState(null);
  const [overviewSelectedYear, setOverviewSelectedYear] =
    useState(DEFAULT_YEAR);
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const title = formatDashboardTitle(dashboard?.eda_name || "Cash Flow");

  useEffect(() => {
    let ignore = false;
    const fetchPeriods = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          "/eda4/cashflow-period-dropdown",
        );
        if (!ignore && response.data?.status) {
          const items = response.data.items;
          const months = items.months || [];
          const weeks = items.weeks || [];
          setMonthPeriods(months);
          setWeekPeriods(weeks);

          if (months.length > 0) {
            setSelectedMonthPeriod(months[0]);
            setMisSelectedMonthPeriod(months[0]);
          }
          if (weeks.length > 0) {
            setSelectedWeekPeriod(weeks[0]);
          }
        }
      } catch (err) {
        if (!ignore) {
          setError("Failed to fetch period options.");
          console.error("Failed to fetch period options", err);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchPeriods();
    return () => {
      ignore = true;
    };
  }, []);

  // Fetch main dashboard data
  useEffect(() => {
    if (
      activeTab !== "summary" ||
      !location.state ||
      !selectedMonthPeriod ||
      !selectedWeekPeriod ||
      !selectedYear
    ) {
      return;
    }

    let ignore = false;
    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.post("/eda4/summary-dashboard", {
          Month: selectedMonthPeriod.value,
          Year: String(selectedYear),
          Week: selectedWeekPeriod.value,
        });
        if (!ignore && response.data?.status) {
          setDashboardData(response.data.items);
        } else if (!ignore) {
          setError(response.data?.message || "Failed to fetch dashboard data.");
          setDashboardData(null);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message ||
              "Failed to fetch dashboard data.",
          );
          setDashboardData(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, [
    activeTab,
    location.state,
    selectedYear,
    selectedMonthPeriod,
    selectedWeekPeriod,
  ]);

  // Fetching MIS report data
  useEffect(() => {
    if (activeTab !== "mis" || !misSelectedMonthPeriod || !misSelectedYear) {
      return;
    }

    let ignore = false;
    const fetchMisReportData = async () => {
      setMisLoading(true);
      setMisError("");
      setMisReportData(null);
      try {
        const response = await axiosInstance.post("/eda4/mis-report", {
          Month: misSelectedMonthPeriod.value,
          Year: String(misSelectedYear),
        });
        if (!ignore && response.data?.status) {
          setMisReportData(response.data.items);
        } else if (!ignore) {
          setMisError(response.data?.message || "Failed to fetch MIS report.");
        }
      } catch (apiError) {
        if (!ignore) {
          setMisError(
            apiError?.response?.data?.message || "Failed to fetch MIS report.",
          );
        }
      } finally {
        if (!ignore) {
          setMisLoading(false);
        }
      }
    };

    fetchMisReportData();
    return () => {
      ignore = true;
    };
  }, [activeTab, misSelectedMonthPeriod, misSelectedYear]);

  // Fetching Overview month dropdown
  useEffect(() => {
    if (activeTab !== "overview") {
      return;
    }

    let ignore = false;
    const fetchOverviewMonths = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      try {
        const response = await axiosInstance.get(
          "/eda4/cashflow-month-dropdown",
        );
        if (!ignore && response.data?.status) {
          const months = response.data.items || [];
          setOverviewMonths(months);
          if (months.length > 0) {
            const currentMonth = months.find((m) =>
              m.label.includes("Current Month"),
            );
            setOverviewSelectedMonth(currentMonth || months[0]);
          }
        } else if (!ignore) {
          setOverviewError(
            response.data?.message || "Failed to fetch overview months.",
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setOverviewError(
            apiError?.response?.data?.message ||
              "Failed to fetch overview months.",
          );
        }
      } finally {
        if (!ignore) {
          setOverviewLoading(false);
        }
      }
    };

    fetchOverviewMonths();
    return () => {
      ignore = true;
    };
  }, [activeTab]);

  // Fetching Overview data
  useEffect(() => {
    if (
      activeTab !== "overview" ||
      !overviewSelectedMonth ||
      !overviewSelectedYear
    ) {
      return;
    }

    let ignore = false;
    const fetchOverviewData = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      setOverviewData(null);
      try {
        const response = await axiosInstance.post("/eda4/overview-dashboard", {
          Month: overviewSelectedMonth.value,
          Year: String(overviewSelectedYear),
        });

        if (!ignore && response.data?.status) {
          setOverviewData(response.data.items);
        } else if (!ignore) {
          setOverviewError(
            response.data?.message || "Failed to fetch overview data.",
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setOverviewError(
            apiError?.response?.data?.message ||
              "Failed to fetch overview data.",
          );
        }
      } finally {
        if (!ignore) {
          setOverviewLoading(false);
        }
      }
    };

    fetchOverviewData();

    return () => {
      ignore = true;
    };
  }, [activeTab, overviewSelectedMonth, overviewSelectedYear]);

  const handleReset = () => {
    if (monthPeriods.length > 0) {
      setSelectedMonthPeriod(monthPeriods[0]);
    }
    if (weekPeriods.length > 0) {
      setSelectedWeekPeriod(weekPeriods[0]);
    }
    setSelectedYear(DEFAULT_YEAR);
  };

  const handleOverviewReset = () => {
    if (overviewMonths.length > 0) {
      const currentMonth = overviewMonths.find((m) =>
        m.label.includes("Current Month"),
      );
      setOverviewSelectedMonth(currentMonth || overviewMonths[0]);
    }
    setOverviewSelectedYear(DEFAULT_YEAR);
  };

  const handleMisReset = () => {
    if (monthPeriods.length > 0) {
      setMisSelectedMonthPeriod(monthPeriods[0]);
    }
    setMisSelectedYear(DEFAULT_YEAR);
  };

  const TabButton = ({ label, tabName }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tabName)}
      className={`px-4 py-3 text-sm font-semibold transition-colors duration-200 focus:outline-none ${
        activeTab === tabName
          ? "border-b-2 border-blue-600 text-blue-600"
          : "text-slate-500 hover:text-slate-800"
      }`}
    >
      {label}
    </button>
  );

  const hasSummaryData = useMemo(() => {
    if (!dashboardData) return false;

    return Object.values(dashboardData).some((section) => {
      // Investment Summary
      if (Array.isArray(section)) {
        return section.length > 0;
      }

      // Operations / Equity & Investment
      return (
        (section?.CashInFlow?.length ?? 0) > 0 ||
        (section?.CashOutFlow?.length ?? 0) > 0
      );
    });
  }, [dashboardData]);

  return { navigate, location, dashboard, selectedProcess, monthPeriods, weekPeriods, selectedMonthPeriod, setSelectedMonthPeriod, selectedWeekPeriod, setSelectedWeekPeriod, selectedYear, setSelectedYear, misSelectedMonthPeriod, setMisSelectedMonthPeriod, misSelectedYear, setMisSelectedYear, misReportData, misLoading, misError, activeTab, setActiveTab, overviewMonths, overviewSelectedMonth, setOverviewSelectedMonth, overviewSelectedYear, setOverviewSelectedYear, overviewData, overviewLoading, overviewError, dashboardData, loading, error, handleReset, handleOverviewReset, handleMisReset, hasSummaryData };
};
